import type { Capabilities, PriceRules } from './models';

/**
 * 计价器（纯函数，前后端共用）。
 * 官方用量估算公式：tokens = (输入视频时长 + 输出时长) × 宽 × 高 × fps / 1024
 */
export interface QuoteInput {
  resolution: string;
  ratio: string;
  /** -1 表示由模型自动决定时长，按能力上限估算 */
  duration: number;
  /** 输入参考视频总时长（秒） */
  inputVideoSeconds: number;
  hasVideoInput: boolean;
}

export function outputPixels(caps: Capabilities, resolution: string, ratio: string): [number, number] {
  const table = caps.pixels[resolution];
  if (!table) throw new Error(`unsupported resolution ${resolution}`);
  if (ratio === 'adaptive') {
    // 自适应比例：取该分辨率下像素最多的比例作为上界
    let best: [number, number] = [0, 0];
    for (const dims of Object.values(table)) if (dims[0] * dims[1] > best[0] * best[1]) best = dims;
    return best;
  }
  const dims = table[ratio];
  if (!dims) throw new Error(`unsupported ratio ${ratio}`);
  return dims;
}

/**
 * 冻结用的 token 上界。上游实测帧数 = 秒数 × fps + 1（5 秒 24fps 为 121 帧；
 * 例：720p 16:9 4 秒 usage.completion_tokens = 97 × 1280 × 720 / 1024 = 87300），少算一帧会让结算超出冻结。
 */
export function maxTokens(caps: Capabilities, q: QuoteInput): number {
  const [w, h] = outputPixels(caps, q.resolution, q.ratio);
  const outSeconds = q.duration === -1 ? caps.duration.max : q.duration;
  const frames = (q.inputVideoSeconds + outSeconds) * caps.fps + 1;
  // 上游实际像素会按编码块对齐，usage 略高于公式（实测 480p 16:9 4 秒：公式 38828，返回 38830），冻结多留 1%
  return Math.ceil(((frames * w * h) / 1024) * 1.01);
}

function unitPriceMicrosPerMillion(rules: PriceRules, resolution: string, hasVideoInput: boolean): bigint {
  const p = rules.prices[resolution] ?? rules.prices.default;
  if (!p) throw new Error(`no price for resolution ${resolution}`);
  return parseMicros(hasVideoInput ? p.with_video_input : p.no_video_input);
}

function parseMicros(value: string): bigint {
  const [whole = '0', frac = ''] = value.split('.');
  return BigInt(whole) * 1_000_000n + BigInt(frac.padEnd(6, '0').slice(0, 6));
}

/** token 用量 → 客户金额（微元），按规则向上取整。 */
export function chargeForTokens(rules: PriceRules, resolution: string, hasVideoInput: boolean, tokens: number): bigint {
  const billable = hasVideoInput ? Math.max(tokens, rules.min_tokens_with_video_input) : tokens;
  const unit = unitPriceMicrosPerMillion(rules, resolution, hasVideoInput);
  const raw = (BigInt(billable) * unit + 999_999n) / 1_000_000n;
  const step = BigInt(rules.rounding_step_micros);
  return ((raw + step - 1n) / step) * step;
}

export function maxReserveMicros(caps: Capabilities, rules: PriceRules, q: QuoteInput): bigint {
  return chargeForTokens(rules, q.resolution, q.hasVideoInput, maxTokens(caps, q));
}
